import { useQuery } from '@apollo/client';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  ToggleGroup,
  ToggleGroupItem,
  cn,
} from '@newfront-insurance/core-ui/v2';
import { useState } from 'react';
import { useDebounce } from 'react-use';

import { AnalyticsTable, AnalyticsDate, type AnalyticsColumn, type SortDirection } from './analytics-table';
import { AnalyticsUnavailableValue } from './analytics-unavailable-value';
import {
  GET_NOVA_SKILL_USAGE,
  GET_NOVA_PERSON_USAGE,
  GET_NOVA_INACTIVE_PEOPLE,
  NOVA_ANALYTICS_INITIAL_PAGE,
  type NovaSkillUsageRow,
  type NovaSkillUsageVariables,
  type NovaPersonUsageRow,
  type NovaPersonUsageVariables,
  type NovaInactivePersonRow,
  type NovaInactivePeopleVariables,
} from '../../../../queries/nova-analytics';

const PAGE_SIZE = NOVA_ANALYTICS_INITIAL_PAGE.limit;
// The dashboard operation primes the initial pages. Only uncached table arguments need another request.
const queryOptions = {
  fetchPolicy: 'cache-first',
  nextFetchPolicy: 'cache-first',
  notifyOnNetworkStatusChange: true,
  context: { notifications: { preventErrorNotification: true } },
} as const;

function useTableControls<Field extends string>(initialField: Field, initialDirection: SortDirection = 'DESC') {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState({ search: '', offset: 0 });
  const [sort, setSort] = useState({ field: initialField, direction: initialDirection });
  // Commit the search and page reset together so typing on a later page never requests the old search's first page.
  useDebounce(() => setQuery((current) => (current.search === search ? current : { search, offset: 0 })), 300, [
    search,
  ]);
  const onPageChange = (offset: number) => setQuery((current) => ({ ...current, offset }));
  return {
    search,
    querySearch: query.search,
    offset: query.offset,
    sortField: sort.field,
    sortDirection: sort.direction,
    limit: PAGE_SIZE,
    onSearchChange: setSearch,
    onSort: (field: Field) => {
      setSort({ field, direction: field === sort.field && sort.direction === 'DESC' ? 'ASC' : 'DESC' });
      onPageChange(0);
    },
    onPageChange,
  };
}

export function EverySkillTable({
  scope,
  initialLoading,
}: {
  scope: NovaSkillUsageVariables['scope'];
  initialLoading: boolean;
}) {
  const controls = useTableControls<NonNullable<NovaSkillUsageVariables['sortField']>>('RUN_COUNT');
  const [shareState, setShareState] = useState<'ALL' | 'PERSONAL' | 'SHARED'>('ALL');
  const { data, loading, error, refetch } = useQuery(GET_NOVA_SKILL_USAGE, {
    ...queryOptions,
    skip: initialLoading,
    variables: {
      scope,
      filter: { search: controls.querySearch, shareState: shareState === 'ALL' ? null : shareState },
      sortField: controls.sortField,
      sortDirection: controls.sortDirection,
      page: { offset: controls.offset, limit: PAGE_SIZE },
    },
  });
  const page = data?.getNovaSkillUsage;
  const columns: AnalyticsColumn<NovaSkillUsageRow, NonNullable<NovaSkillUsageVariables['sortField']>>[] = [
    {
      key: 'skill',
      label: 'Skill',
      className: 'w-[29%]',
      render: (row) => <span className="font-semibold">/{row.skillName}</span>,
    },
    {
      key: 'owner',
      label: 'Owner',
      className: 'w-[17%]',
      render: (row) => row.ownerName ?? (row.shareState === 'BUILT_IN' ? 'Built-in' : <AnalyticsUnavailableValue />),
    },
    {
      key: 'people',
      label: 'People who ran it',
      sortField: 'RUNNER_COUNT',
      align: 'right',
      className: 'w-[21%]',
      render: (row) => row.runnerCount30d.toLocaleString('en-US'),
    },
    {
      key: 'runs',
      label: 'Times ran (30d)',
      sortField: 'RUN_COUNT',
      align: 'right',
      className: 'w-[18%]',
      render: (row) => row.runCount30d.toLocaleString('en-US'),
    },
    {
      key: 'lastRun',
      label: 'Last ran',
      sortField: 'LAST_RUN_AT',
      align: 'right',
      className: 'w-[15%]',
      render: (row) => (row.lastRunAt ? <AnalyticsDate value={row.lastRunAt} asOfDate={page?.asOfDate} /> : 'Never'),
    },
  ];
  return (
    <AnalyticsTable
      title="Every skill"
      testId="nova-analytics-every-skill"
      {...controls}
      columns={columns}
      rows={page?.items ?? []}
      rowKey={(row) => row.skillKey}
      totalCount={page?.totalCount ?? 0}
      loading={initialLoading || loading}
      error={Boolean(error) || (!initialLoading && !loading && !page)}
      onRetry={() => {
        void refetch().catch(() => undefined);
      }}
      emptyMessage={controls.querySearch ? 'No skills match your search.' : 'No skills in this view.'}
      toolbar={
        <ToggleGroup
          type="single"
          aria-label="Skill sharing"
          value={shareState}
          onValueChange={(value) => {
            if (value === 'ALL' || value === 'PERSONAL' || value === 'SHARED') {
              setShareState(value);
              controls.onPageChange(0);
            }
          }}
          className="gap-0 rounded-sm"
        >
          {(['ALL', 'PERSONAL', 'SHARED'] as const).map((value) => (
            <ToggleGroupItem
              key={value}
              value={value}
              data-testid={`nova-analytics-sharing-${value.toLowerCase()}`}
              className={cn(
                'border-steel-200 text-steel-400 -ml-px h-8 rounded-none border bg-white px-4 py-1 font-normal',
                'first:ml-0 first:rounded-l-sm last:rounded-r-sm data-[state=on]:font-semibold',
                'data-[state=on]:enabled:border-steel-200 data-[state=on]:enabled:bg-brand-100 data-[state=on]:enabled:text-brand-400',
              )}
            >
              {value.charAt(0) + value.slice(1).toLowerCase()}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      }
    />
  );
}

function PersonSkills({ person }: { person: NovaPersonUsageRow }) {
  const skillCount = `${person.skillsUsedCount30d} ${person.skillsUsedCount30d === 1 ? 'skill' : 'skills'}`;
  if (person.skillsUsedCount30d === 0) return <span>0 skills</span>;
  if (!person.skillsUsed)
    return (
      <span>
        {skillCount}
        <span className="text-steel-400 block text-xs">Details unavailable</span>
      </span>
    );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-brand-400 rounded-sm font-semibold hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
          aria-label={`Skills used by ${person.fullName}`}
          data-testid="nova-analytics-person-skills-trigger"
        >
          {skillCount}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-w-[calc(100vw-2rem)] text-sm"
        aria-label={`Skills used by ${person.fullName}`}
        data-testid="nova-analytics-person-skills-popover"
      >
        <p className="text-steel-500 font-semibold">Skills used by {person.fullName}</p>
        <p className="text-steel-400 mt-1">Last 30 days</p>
        <ul className="text-steel-500 mt-3 flex max-h-60 flex-col gap-2 overflow-y-auto">
          {person.skillsUsed.map((skill, index) => (
            <li key={`${index}:${skill}`}>/{skill}</li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export function UsageByPersonTable({
  scope,
  initialLoading,
}: {
  scope: NovaPersonUsageVariables['scope'];
  initialLoading: boolean;
}) {
  const controls = useTableControls<NonNullable<NovaPersonUsageVariables['sortField']>>('MESSAGE_COUNT');
  const { data, loading, error, refetch } = useQuery(GET_NOVA_PERSON_USAGE, {
    ...queryOptions,
    skip: initialLoading,
    variables: {
      scope,
      search: controls.querySearch,
      sortField: controls.sortField,
      sortDirection: controls.sortDirection,
      page: { offset: controls.offset, limit: PAGE_SIZE },
    },
  });
  const page = data?.getNovaPersonUsage;
  const columns: AnalyticsColumn<NovaPersonUsageRow, NonNullable<NovaPersonUsageVariables['sortField']>>[] = [
    {
      key: 'name',
      label: 'Name',
      className: 'w-[18%]',
      render: (row) => <span className="font-semibold">{row.fullName}</span>,
    },
    {
      key: 'role',
      label: 'Role',
      className: 'w-[19%]',
      render: (row) => row.roleLabel ?? <AnalyticsUnavailableValue />,
    },
    {
      key: 'messages',
      label: 'Messages sent (30d)',
      sortField: 'MESSAGE_COUNT',
      align: 'right',
      className: 'w-[25%]',
      render: (row) => row.messageCount30d.toLocaleString('en-US'),
    },
    {
      key: 'skills',
      label: 'Skills they use',
      sortField: 'SKILLS_USED',
      align: 'right',
      className: 'w-[18%]',
      render: (row) => <PersonSkills person={row} />,
    },
    {
      key: 'lastUsed',
      label: 'Last used Nova',
      sortField: 'LAST_USED_AT',
      align: 'right',
      className: 'w-[20%]',
      render: (row) => (row.lastUsedAt ? <AnalyticsDate value={row.lastUsedAt} asOfDate={page?.asOfDate} /> : 'Never'),
    },
  ];
  return (
    <AnalyticsTable
      title="Usage by person"
      testId="nova-analytics-usage-by-person"
      {...controls}
      columns={columns}
      rows={page?.items ?? []}
      rowKey={(row) => row.personKey}
      totalCount={page?.totalCount ?? 0}
      loading={initialLoading || loading}
      error={Boolean(error) || (!initialLoading && !loading && !page)}
      onRetry={() => {
        void refetch().catch(() => undefined);
      }}
      emptyMessage={controls.querySearch ? 'No people match your search.' : 'No usage data is available for this team.'}
    />
  );
}

export function InactivePeopleTable({
  scope,
  initialLoading,
}: {
  scope: NovaInactivePeopleVariables['scope'];
  initialLoading: boolean;
}) {
  const controls = useTableControls<NonNullable<NovaInactivePeopleVariables['sortField']>>('LAST_USED_AT', 'ASC');
  const { data, loading, error, refetch } = useQuery(GET_NOVA_INACTIVE_PEOPLE, {
    ...queryOptions,
    skip: initialLoading,
    variables: {
      scope,
      search: controls.querySearch,
      sortField: controls.sortField,
      sortDirection: controls.sortDirection,
      page: { offset: controls.offset, limit: PAGE_SIZE },
    },
  });
  const page = data?.getNovaInactivePeople;
  const columns: AnalyticsColumn<NovaInactivePersonRow, NonNullable<NovaInactivePeopleVariables['sortField']>>[] = [
    {
      key: 'name',
      label: 'Colleague',
      className: 'w-[29%]',
      render: (row) => <span className="font-semibold">{row.fullName}</span>,
    },
    {
      key: 'role',
      label: 'Role',
      className: 'w-[27%]',
      render: (row) => row.roleLabel ?? <AnalyticsUnavailableValue />,
    },
    {
      key: 'granted',
      label: 'Access granted',
      sortField: 'ACCESS_GRANTED_AT',
      align: 'right',
      className: 'w-[22%]',
      render: (row) => <AnalyticsDate value={row.accessGrantedAt} asOfDate={page?.asOfDate} />,
    },
    {
      key: 'lastUsed',
      label: 'Last used Nova',
      sortField: 'LAST_USED_AT',
      align: 'right',
      className: 'w-[22%]',
      render: (row) =>
        row.lastUsedAt ? (
          <AnalyticsDate value={row.lastUsedAt} asOfDate={page?.asOfDate} />
        ) : (
          <span className="text-fire-500">Never</span>
        ),
    },
  ];
  let emptyMessage = 'Everyone in this team has used Nova in the last 30 days.';
  if (controls.querySearch) emptyMessage = 'No colleagues match your search.';
  else if (page?.rosterAvailable === false)
    emptyMessage = 'The access roster is not available yet. Colleagues who have never used Nova cannot be identified.';
  return (
    <AnalyticsTable
      title="Who isn’t using Nova"
      testId="nova-analytics-inactive-people"
      {...controls}
      columns={columns}
      rows={page?.items ?? []}
      rowKey={(row) => row.personKey}
      totalCount={page?.totalCount ?? 0}
      loading={initialLoading || loading}
      error={Boolean(error) || (!initialLoading && !loading && !page)}
      onRetry={() => {
        void refetch().catch(() => undefined);
      }}
      emptyMessage={emptyMessage}
      description={
        page?.rosterAvailable === false && Boolean(page.items.length)
          ? 'The access roster is not available yet. This list excludes colleagues who have never used Nova.'
          : undefined
      }
    />
  );
}
